import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, X, PartyPopper, Image, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { createMessage } from "@/services/messages";
import { uploadMessageAttachment, uploadMediaAttachment } from "@/services/storage";
import { createContribution } from "@/services/contributions";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import GifPicker from "@/components/card/GifPicker";
import MediaRecorderWidget from "@/components/card/MediaRecorderWidget";
import { useAuth } from "@/hooks/useAuth";
import SuccessCelebration from "@/components/card/SuccessCelebration";
import { AiWriteButton } from "@/components/ui/ai-write-button";
import Logo from "@/components/Logo";
import type { Tables } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { getQuickSuggestions } from "@/lib/quick-suggestions";
import { useCurrency } from "@/hooks/useCurrency";

interface ContributeWizardProps {
  collection: Tables<"collections">;
  templateUrl: string;
  messages: Tables<"messages">[];
  chipIn: boolean;
  onClose: () => void;
  onSigned: () => void;
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";
const MAX_FILE_SIZE = 5 * 1024 * 1024;

type Step = "sign" | "details" | "amount";

const ContributeWizard = ({
  collection,
  templateUrl,
  messages,
  chipIn,
  onClose,
  onSigned,
}: ContributeWizardProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const currency = useCurrency();
  const [wantsChipIn] = useState(chipIn && collection.collection_enabled);

  const [successType, setSuccessType] = useState<"wish" | "chipIn" | null>(null);

  // Step state — chip-in flow starts at "amount", wish-only starts at "sign"
  const [currentStep, setCurrentStep] = useState<Step>(
    chipIn && collection.collection_enabled ? "amount" : "sign"
  );

  // Sign step
  const [messageText, setMessageText] = useState("");
  const [fromName] = useState(() => user?.user_metadata?.display_name || user?.email || "");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentType, setAttachmentType] = useState<"photo" | "gif" | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Media recording
  const [mediaBlob, setMediaBlob] = useState<Blob | null>(null);
  const [mediaType, setMediaType] = useState<"video" | "audio" | null>(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null);
  const [mediaUploading, setMediaUploading] = useState(false);

  // Details step — pre-fill from auth when logged in
  const isLoggedIn = !!user;
  const displayName = user?.user_metadata?.display_name || "";
  const [firstName, setFirstName] = useState(() => displayName.split(" ")[0] || "");
  const [lastName, setLastName] = useState(() => displayName.split(" ").slice(1).join(" ") || "");
  const [email, setEmail] = useState(() => user?.email || "");
  const [agreeTerms, setAgreeTerms] = useState(isLoggedIn);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  // Amount step
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const captchaRequired = !!TURNSTILE_SITE_KEY;

  // For logged-in wish-only: single step, no progress needed
  const isSingleStep = isLoggedIn && !wantsChipIn;
  const totalStepsForProgress = wantsChipIn
    ? (isLoggedIn ? 2 : 3)
    : (isLoggedIn ? 1 : 2);
  const stepsForProgress = wantsChipIn
    ? (currentStep === "amount" ? 1 : currentStep === "sign" ? 2 : 3)
    : (currentStep === "sign" ? 1 : 2);
  const progress = (stepsForProgress / totalStepsForProgress) * 100;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ variant: "destructive", title: "Invalid file", description: "Please upload an image file." });
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast({ variant: "destructive", title: "File too large", description: "Please upload an image under 5 MB." });
      return;
    }
    setUploading(true);
    const { publicUrl, error } = await uploadMessageAttachment(file);
    if (error) {
      toast({ variant: "destructive", title: "Upload failed", description: error.message });
    } else if (publicUrl) {
      setAttachmentUrl(publicUrl);
      setAttachmentType("photo");
    }
    setUploading(false);
  };

  const clearAttachment = () => {
    setAttachmentUrl("");
    setAttachmentType(null);
  };

  const handleMediaRecorded = (blob: Blob, type: "video" | "audio") => {
    setMediaBlob(blob);
    setMediaType(type);
    setMediaPreviewUrl(URL.createObjectURL(blob));
  };

  const clearMedia = () => {
    if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl);
    setMediaBlob(null);
    setMediaType(null);
    setMediaPreviewUrl(null);
  };

  const uploadMedia = async (): Promise<string | null> => {
    if (!mediaBlob || !mediaType) return null;
    setMediaUploading(true);
    const { publicUrl, error } = await uploadMediaAttachment(mediaBlob, mediaType);
    setMediaUploading(false);
    if (error) {
      toast({ variant: "destructive", title: "Upload failed", description: error.message });
      return null;
    }
    return publicUrl;
  };


  const goBack = () => {
    if (currentStep === "amount") onClose();
    else if (currentStep === "sign") {
      if (wantsChipIn) setCurrentStep("amount");
      else onClose();
    }
    else if (currentStep === "details") setCurrentStep("sign");
  };

  const goNext = () => {
    if (currentStep === "amount") {
      setCurrentStep("sign");
    } else if (currentStep === "sign") {
      if (wantsChipIn) {
        if (isLoggedIn) {
          handleSubmitWithContribution();
        } else {
          setCurrentStep("details");
        }
      } else {
        if (isLoggedIn) {
          handleSubmitSignOnly();
        } else {
          setCurrentStep("details");
        }
      }
    } else if (currentStep === "details") {
      if (wantsChipIn) {
        handleSubmitWithContribution();
      } else {
        handleSubmitSignOnly();
      }
    }
  };

  const handleSkipSign = () => {
    if (isLoggedIn) {
      handleSubmitWithContribution();
    } else {
      setCurrentStep("details");
    }
  };

  const handleSubmitSignOnly = async () => {
    setSubmitting(true);
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim() || fromName.trim();

    // Upload media if recorded
    const mediaUrl = await uploadMedia();

    const { error } = await createMessage({
      collection_id: collection.id,
      contributor_name: fullName,
      contributor_email: email.trim() || null,
      message_text: messageText.trim(),
      photo_url: attachmentType === "photo" ? attachmentUrl : null,
      gif_url: attachmentType === "gif" ? attachmentUrl : null,
      video_url: mediaType === "video" ? mediaUrl : null,
      audio_url: mediaType === "audio" ? mediaUrl : null,
    } as any);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } else {
      notifyOrganizer("signature", fullName, messageText.trim());
      setSuccessType("wish");
    }
    setSubmitting(false);
  };

  const notifyOrganizer = (
    activityType: "signature" | "contribution",
    signerName: string,
    messagePreview?: string,
  ) => {
    // Fire-and-forget — organizer notifications must never block the guest flow
    supabase.functions
      .invoke("notify-organizer", {
        body: {
          collectionId: collection.id,
          activityType,
          signerName,
          messagePreview: messagePreview || undefined,
        },
      })
      .catch((err) => {
        console.error("notify-organizer failed", err);
      });
  };

  const handleSubmitWithContribution = async () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount < 1) return;

    setSubmitting(true);
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim() || fromName.trim();

    // Upload media if recorded
    const mediaUrl = await uploadMedia();

    // First sign the card (only when the user actually wrote/attached something)
    const hasMessageContent = messageText.trim().length > 0 || !!attachmentUrl || !!mediaBlob;
    if (hasMessageContent) {
      const { error: msgError } = await createMessage({
        collection_id: collection.id,
        contributor_name: fullName,
        contributor_email: email.trim() || null,
        message_text: messageText.trim(),
        photo_url: attachmentType === "photo" ? attachmentUrl : null,
        gif_url: attachmentType === "gif" ? attachmentUrl : null,
        video_url: mediaType === "video" ? mediaUrl : null,
        audio_url: mediaType === "audio" ? mediaUrl : null,
      } as any);
      if (msgError) {
        toast({ variant: "destructive", title: "Error", description: msgError.message });
        setSubmitting(false);
        return;
      }
      notifyOrganizer("signature", fullName, messageText.trim());
    }

    // Then create contribution
    try {
      const { data, error } = await createContribution({
        collection_id: collection.id,
        amount: numAmount,
        contributor_name: fullName,
        contributor_email: email.trim(),
        captcha_token: captchaToken || "",
      });
      if (error) throw error;
      if (data?.url) {
        const stripeWindow = window.open(data.url, "_blank");
        if (!stripeWindow) {
          // Popup blocked — fallback to same-tab redirect
          window.location.href = data.url;
        }
        toast({ title: "Payment opened", description: "Complete your payment in the new tab, then return here." });
        onSigned();
      } else {
        throw new Error("No payment URL returned");
      }
    } catch (err: unknown) {
      toast({ variant: "destructive", title: "Error", description: err instanceof Error ? err.message : "Failed to start payment" });
      turnstileRef.current?.reset();
      setCaptchaToken(null);
    }
    setSubmitting(false);
  };

  const canContinueSign = messageText.trim().length > 0;
  const canContinueDetails = firstName.trim().length > 0 && lastName.trim().length > 0 && email.trim().length > 0 && agreeTerms && (!captchaRequired || !!captchaToken);
  
  const canContinueAmount = amount && parseFloat(amount) >= 1;

  if (successType) {
    return (
      <AnimatePresence>
        <SuccessCelebration type={successType} onDone={onSigned} />
      </AnimatePresence>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background flex"
    >
      {/* Left branded panel */}
      <div className="hidden md:flex w-1/2 bg-primary/10 flex-col items-center justify-center p-12 relative">
        <div className="absolute top-8 left-8">
          <Logo size="lg" />
        </div>

        {templateUrl ? (
          <div className="w-full max-w-md">
            <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-xl bg-white">
              <img src={templateUrl} alt={collection.title} className="w-full h-full object-cover" />
            </div>
            {messages.length > 0 && (
              <p className="text-center text-sm text-muted-foreground mt-4">
                {messages.length} of {messages.length} messages
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            {/* Decorative illustration placeholder */}
            <div className="w-64 h-48 flex items-center justify-center text-primary/30">
              <PartyPopper size={80} />
            </div>
          </div>
        )}
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        {/* Top bar with back + optional progress */}
        <div className="sticky top-0 bg-background z-10 px-6 py-4 flex items-center gap-4">
          <button onClick={goBack} className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={20} />
          </button>
          {/* Only show progress bar if multi-step */}
          {!isSingleStep && (
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
          )}
        </div>

        {/* Step content */}
        <div className="flex-1 flex items-start justify-center px-6 py-8">
          <div className="w-full max-w-md">
            <AnimatePresence mode="wait">
              {currentStep === "sign" && (
                <motion.div
                  key="sign"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-5"
                >
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-foreground font-serif">Sign the Card</h2>
                    <p className="text-sm text-muted-foreground mt-1">Add your message to the card.</p>
                  </div>

                  {/* Suggested messages */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-[0.12em]">Quick suggestions</p>
                    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none" style={{ scrollbarWidth: "none" }}>
                      {getQuickSuggestions(collection.occasion).map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => setMessageText(suggestion)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap flex-shrink-0 max-w-[200px] truncate ${
                            messageText === suggestion
                              ? "bg-primary/10 border-primary text-primary"
                              : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                          }`}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-[0.12em]">Your message</Label>
                      <AiWriteButton
                        context={{
                          occasion: collection.occasion,
                          recipientName: collection.recipient_name,
                          senderName: fromName,
                        }}
                        fieldPurpose="personal_wish"
                        onGenerated={(text) => setMessageText(text)}
                      />
                    </div>
                    <Textarea
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder="Write your heartfelt message here..."
                      className="mt-2 border-border min-h-[100px] resize-none"
                      rows={4}
                    />
                  </div>

                  {/* Media buttons — compact row */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowGifPicker(!showGifPicker)}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full border border-border text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <Film size={16} className="text-muted-foreground" /> GIF
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full border border-border text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                    >
                      <Image size={16} className="text-muted-foreground" /> {uploading ? "..." : "Photo"}
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                  </div>

                  {/* Attachment preview */}
                  {attachmentUrl && (
                    <div className="relative w-fit">
                      {attachmentType === "gif" ? (
                        <img src={attachmentUrl} alt="GIF" className="max-h-40 w-auto rounded-xl border border-border object-contain" />
                      ) : (
                        <img src={attachmentUrl} alt="Photo" className="h-28 w-auto rounded-xl border border-border object-cover" />
                      )}
                      <button onClick={clearAttachment} className="absolute -top-2 -right-2 w-6 h-6 bg-foreground text-background rounded-full flex items-center justify-center shadow-md">
                        <X size={12} />
                      </button>
                    </div>
                  )}

                  <GifPicker
                    open={showGifPicker && !attachmentUrl}
                    onSelect={(url) => { setAttachmentUrl(url); setAttachmentType("gif"); setShowGifPicker(false); }}
                    onClose={() => setShowGifPicker(false)}
                  />

                  {/* Video / Audio recording — compact */}
                  <MediaRecorderWidget
                    onRecorded={handleMediaRecorded}
                    onClear={clearMedia}
                    recordedUrl={mediaPreviewUrl}
                    recordedType={mediaType}
                    disabled={uploading || mediaUploading}
                  />

                  <Button
                    onClick={goNext}
                    disabled={!canContinueSign || submitting}
                    className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base"
                    size="lg"
                  >
                    {submitting ? "Sending..." : wantsChipIn ? "Continue to payment" : isLoggedIn ? "Send Wish" : "Continue"}
                  </Button>

                  {wantsChipIn && (
                    <button
                      type="button"
                      onClick={handleSkipSign}
                      disabled={submitting}
                      className="w-full text-sm font-medium text-muted-foreground hover:text-foreground underline underline-offset-4 disabled:opacity-50"
                    >
                      Skip to payment
                    </button>
                  )}
                </motion.div>
              )}

              {currentStep === "details" && (
                <motion.div
                  key="details"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-primary font-serif">Your details</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      We'll send you a copy of the card in case you need to edit your message or image.
                    </p>
                  </div>

                  <div className="border border-border rounded-xl p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-foreground">First Name</Label>
                        <Input
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="First Name"
                          className="mt-1.5 border-border"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-foreground">Last Name</Label>
                        <Input
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Last Name"
                          className="mt-1.5 border-border"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-foreground">Email Address</Label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Email Address"
                        className="mt-1.5 border-border"
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="terms"
                      checked={agreeTerms}
                      onCheckedChange={(checked) => setAgreeTerms(checked === true)}
                    />
                    <label htmlFor="terms" className="text-sm text-muted-foreground leading-snug cursor-pointer">
                      I agree to JOYN's{" "}
                      <a href="/terms" className="underline text-primary">Terms</a>.
                    </label>
                  </div>

                  {TURNSTILE_SITE_KEY && (
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(null)}
                      onError={() => setCaptchaToken(null)}
                    />
                  )}

                  <Button
                    onClick={goNext}
                    disabled={!canContinueDetails || submitting}
                    className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base"
                    size="lg"
                  >
                    {submitting ? "Submitting..." : "Continue"}
                  </Button>
                </motion.div>
              )}

              {currentStep === "amount" && (
                <motion.div
                  key="amount"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-foreground font-serif">
                      How much would you like to contribute?
                    </h2>
                  </div>

                  <div className="flex items-center justify-center gap-1">
                    <span className="px-3 py-2.5 bg-muted rounded-l-lg text-sm font-medium text-muted-foreground border border-border border-r-0">
                      {currency}
                    </span>
                    <Input
                      type="number"
                      min="1"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder=""
                      className="w-24 rounded-l-none border-border text-center"
                    />
                  </div>
                  <p className="text-center text-sm text-muted-foreground">
                    The amount you give won't be seen by others
                  </p>

                  {/* Payment summary */}
                  {amount && parseFloat(amount) >= 1 && (
                    <div className="border border-border rounded-xl p-5 space-y-3">
                      <h3 className="text-sm font-semibold text-foreground">Payment Summary</h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Chip-in amount</span>
                          <span className="text-foreground">{currency} {parseFloat(amount).toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Platform fee</span>
                          <span className="text-foreground">{currency} 10.00</span>
                        </div>
                        <div className="border-t border-border pt-2 flex items-center justify-between text-sm font-semibold">
                          <span className="text-foreground">Total</span>
                          <span className="text-foreground">{currency} {(parseFloat(amount) + 10).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={goNext}
                    disabled={!canContinueAmount || submitting}
                    className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base"
                    size="lg"
                  >
                    Continue
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ContributeWizard;
